/**
 * Normalizes axios/FastAPI/SSTF-BFF error payloads into a single user-facing string.
 *
 * Covers the **legacy** `apiClient` fallback
 * `data.detail || data.message || err.message`, plus:
 * - Flask / incident API: `{ success?: boolean, error: string }`
 * - SSTF BFF: `{ message, errors?: [{ message, reason }] }` (body or embedded in `error`)
 * - Trailing JSON inside long `error` strings from upstream bridges
 */

import { INCIDENT_HTTP_FALLBACK_HINT_JA } from "./incidentReportKeys";

function formatSstfBffErrorBody(body: Record<string, unknown>): string {
  const parts: string[] = [];
  const top = body.message;
  if (typeof top === "string" && top.trim()) parts.push(top.trim());
  const errors = body.errors;
  if (Array.isArray(errors)) {
    for (const e of errors) {
      if (e && typeof e === "object") {
        const m = (e as { message?: unknown }).message;
        const r = (e as { reason?: unknown }).reason;
        if (typeof m === "string" && m.trim()) parts.push(m.trim());
        else if (typeof r === "string" && r.trim()) parts.push(r.trim());
      }
    }
  }
  return Array.from(new Set(parts)).join(" — ");
}

function formatFastApiDetail(detail: unknown): string {
  if (typeof detail === "string") return detail.trim();
  if (Array.isArray(detail)) {
    return detail
      .map((x: unknown) => {
        if (x && typeof x === "object" && "msg" in (x as object)) {
          const msg = (x as { msg?: unknown }).msg;
          return typeof msg === "string" ? msg : "";
        }
        if (typeof x === "string") return x;
        return "";
      })
      .filter(Boolean)
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }
  return "";
}

/** Parse trailing `{...}` only when it looks like SSTF/API JSON (not a stray `{` in prose). */
function tryParseTrailingJsonObject(s: string): Record<string, unknown> | null {
  const idx = s.lastIndexOf("{");
  if (idx === -1) return null;
  const slice = s.slice(idx).trim();
  if (slice.length < 15) return null;
  if (!/"(?:message|errors|code|detail)"/.test(slice)) return null;
  try {
    const parsed: unknown = JSON.parse(slice);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * 文字列に埋め込まれた JSON を展開して、内側の利用者向けメッセージを返す。
 *
 * 上流ブリッジ（ai_sstf_controller）は非 200 のとき本文を差し替えて
 * `"AI Assist error: {…JSON…}"` の形で返すため、展開しないと画面のエラー通知に
 * 生の JSON が出る（#163）。展開できないときは `undefined` を返す。
 */
function unwrapEmbeddedJsonMessage(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const s = value.trim();
  if (!s) return undefined;
  const embedded = tryParseTrailingJsonObject(s);
  if (!embedded) return undefined;
  const inner = formatSstfBffErrorBody(embedded);
  return inner || undefined;
}

function isGenericAxiosStatusMessage(msg: string): boolean {
  return /^request failed with status code \d+$/i.test(msg.trim());
}

/** When the server returns no usable JSON/text in the body. */
function fallbackFromHttpStatus(error: unknown): string {
  const err = error as {
    message?: string;
    response?: { status?: number; statusText?: string; data?: unknown };
  };
  const status = err?.response?.status;
  const statusText = err?.response?.statusText;
  const axiosMsg = err?.message?.trim() || "";

  if (status == null) {
    return axiosMsg || "An error occurred";
  }

  const line =
    statusText && statusText.trim()
      ? `HTTP ${status} ${statusText.trim()}`
      : `HTTP ${status}`;

  if (axiosMsg && !isGenericAxiosStatusMessage(axiosMsg)) {
    return `${line}: ${axiosMsg}`;
  }

  return `${line}。${INCIDENT_HTTP_FALLBACK_HINT_JA}`;
}

/**
 * Maps an axios error to one string for `ApiResponse.error`.
 * Prefer structured `response.data` fields; then fall back like the older
 * `apiClient` chain (`detail` → `message` → `err.message` → generic).
 */
export function formatAxiosErrorMessage(error: unknown): string {
  const err = error as {
    message?: string;
    response?: { status?: number; statusText?: string; data?: unknown };
  };
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (typeof data === "string" && data.trim()) {
    const t = data.trim();
    return t.length > 800 ? `${t.slice(0, 800)}…` : t;
  }

  if (!data || typeof data !== "object") {
    return fallbackFromHttpStatus(error);
  }

  const raw = data as Record<string, unknown>;
  // `error` と同様に `message` / `detail` の埋め込み JSON も展開する。外側の生文字列と
  // 内側のメッセージが二重に出ないよう、以降は展開後の本文だけを見る（#163）。
  //
  // 実際の経路は React → 本リポジトリの FastAPI → controller → ai_assist で、
  // BackendClient が 400 以上を HTTPException(detail=...) に詰め替えるため、
  // ブラウザに届くのは多くの場合 `detail`（utils/backend_client.py:99-108）。
  const d: Record<string, unknown> = { ...raw };
  const unwrappedMessage = unwrapEmbeddedJsonMessage(raw.message);
  if (unwrappedMessage !== undefined) d.message = unwrappedMessage;
  const unwrappedDetail = unwrapEmbeddedJsonMessage(raw.detail);
  if (unwrappedDetail !== undefined) d.detail = unwrappedDetail;
  const out: string[] = [];

  if (typeof d.error === "string" && d.error.trim()) {
    const errStr = d.error.trim();
    const embedded = tryParseTrailingJsonObject(errStr);
    const inner = embedded ? formatSstfBffErrorBody(embedded) : "";
    out.push(inner || errStr);
  }

  const bff = formatSstfBffErrorBody(d);
  if (bff && !out.includes(bff)) {
    out.unshift(bff);
  }

  if (d.detail !== undefined) {
    const dv = formatFastApiDetail(d.detail);
    if (dv && !out.includes(dv)) out.push(dv);
  }

  if (typeof d.message === "string" && d.message.trim()) {
    const m = d.message.trim();
    if (!out.includes(m)) out.push(m);
  }

  const joined = Array.from(
    new Set(out.map((s) => s.trim()).filter(Boolean))
  ).join(" — ");
  if (joined) return joined;
  return fallbackFromHttpStatus(error);
}
